import React, { useState, useEffect, useMemo } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  IconButton, Grid, useMediaQuery, Theme, Button, Card, CardContent, Typography,
  Box, Container, Tabs, Tab, Avatar, TextField, InputAdornment, Tooltip,
  Fab, Dialog, DialogContent, DialogTitle, CircularProgress, TableSortLabel,
  Divider
} from '@mui/material';
import { Edit, Delete, Add as AddIcon, List, ShowChart, TrendingUp, AccountBalance, MonetizationOn, Star, Calculate, Info as InfoIcon } from '@mui/icons-material';
import { useIntl, FormattedMessage } from 'react-intl';
import { Investment } from '../types/Investment';
import { getInvestments, deleteInvestment, addInvestment, updateInvestment } from '../firebase/firestoreOperations';
import InvestmentForm from './InvestmentForm';
import { User } from 'firebase/auth';
import { getMultipleCryptoPrices, getTokenDetails } from '../services/coingeckoService';
import { ArrowDropUp, ArrowDropDown } from '@mui/icons-material';
import { auth } from '../firebase/config';
import { TokenDetails } from '../services/coingeckoService';
import { styled } from '@mui/material/styles';
import { designTokens } from '../theme/designTokens';
import { InvestmentSkeleton } from './LoadingSkeleton';

interface PriceData {
  price: number;
  price_change_percentage_24h: number;
}

interface InvestmentListProps {
  user: User;
}

type SortConfig = {
  key: 'investment' | 'purchasePrice' | 'currentValue' | 'profitLoss' | 'purchaseDate';
  direction: 'asc' | 'desc';
} | null;

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    width: '120px',
  },
}));

const InvestmentList: React.FC<InvestmentListProps> = ({ user }) => {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const [investments, setInvestments] = useState<Investment[]>([]);
  const [editingInvestment, setEditingInvestment] = useState<Investment | undefined>(undefined);
  const [currentPrices, setCurrentPrices] = useState<{ [key: string]: PriceData }>({});
  const [tabValue, setTabValue] = useState(0);
  const intl = useIntl();
  const [tokenDetails, setTokenDetails] = useState<{ [key: string]: TokenDetails }>({});
  const [forecastPrices, setForecastPrices] = useState<{ [key: string]: string }>({});
  const [simulationAmounts, setSimulationAmounts] = useState<{ [key: string]: string }>({});
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState<SortConfig>(null);

  const getTokenId = (token: string): string => {
    switch (token.toLowerCase()) {
      case 'ada':
        return 'cardano';
      case 'eth':
        return 'ethereum';
      case 'btc':
        return 'bitcoin';
      case 'link':
        return 'chainlink';
      case 'night':
        return 'midnight-3';
      default:
        return token.toLowerCase();
    }
  };

  const fetchInvestments = async () => {
    if (user) {
      try {
        console.log('Fetching investments for user:', user.uid);
        const fetchedInvestments = await getInvestments(user.uid);
        console.log('Fetched investments:', fetchedInvestments);
        const cryptoInvestments = fetchedInvestments.filter(inv => inv.type !== 'etf');
        setInvestments(cryptoInvestments);

        const tokens = cryptoInvestments
          .map(inv => inv.token)
          .filter(token => token && typeof token === 'string' && token.trim() !== '');

        if (tokens.length === 0) {
          console.log('No valid tokens to fetch prices/details for');
          return;
        }
        try {
          console.log('Fetching prices for tokens:', tokens);
          const prices = await getMultipleCryptoPrices(tokens);
          console.log('Received prices:', prices);
          setCurrentPrices(prices as { [key: string]: PriceData });

          // Fetch token details including images
          const details = await Promise.all(tokens.map(token => getTokenDetails(token)));
          console.log('Received token details:', details);
          const detailsMap = details.reduce((acc: { [key: string]: TokenDetails }, detail: TokenDetails | null) => {
            if (detail) {
              acc[detail.symbol] = detail;
            }
            return acc;
          }, {});
          console.log('Token details map:', detailsMap);
          setTokenDetails(detailsMap);
        } catch (error) {
          console.error('Error fetching current prices or token details:', error);
        }
      } catch (error) {
        console.error('Error fetching investments:', error);
      }
    }
  };

  useEffect(() => {
    let isMounted = true;
    let intervalId: NodeJS.Timeout;

    const loadData = async () => {
      setIsLoading(true);
      if (user && isMounted) {
        await fetchInvestments();
        setIsLoading(false);

        // Refresh prices every 60 seconds
        intervalId = setInterval(fetchInvestments, 60000);
      }
    };

    loadData();

    return () => {
      isMounted = false;
      if (intervalId) clearInterval(intervalId);
    };
  }, [user]);



  const handleEdit = (investment: Investment) => {
    setEditingInvestment(investment);
    setIsEditFormOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(intl.formatMessage({ id: 'confirmDeleteInvestment' }))) {
      try {
        await deleteInvestment(id, user.uid);
        await fetchInvestments(); // Refresh the list after deletion
      } catch (error) {
        console.error('Error deleting investment:', error);
      }
    }
  };

  const handleOpenForm = () => {
    setIsFormOpen(true);
    setEditingInvestment(undefined);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingInvestment(undefined);
  };

  const handleCloseEditForm = () => {
    setIsEditFormOpen(false);
    setEditingInvestment(undefined);
  };

  const handleAddInvestment = async (investment: Omit<Investment, 'id'>) => {
    if (!auth.currentUser) {
      console.error("User is not authenticated");
      return;
    }

    try {
      console.log('Adding investment:', investment);
      console.log('Current user UID:', auth.currentUser.uid);

      // Fetch token details and current price from CoinGecko
      const tokenId = getTokenId(investment.token);
      const [tokenDetail, priceData] = await Promise.all([
        getTokenDetails(tokenId),
        getMultipleCryptoPrices([tokenId])
      ]);

      if (tokenDetail) {
        setTokenDetails(prevDetails => ({
          ...prevDetails,
          [tokenId]: tokenDetail
        }));
      }

      if (priceData && priceData[tokenId]) {
        setCurrentPrices(prevPrices => ({
          ...prevPrices,
          [tokenId]: priceData[tokenId] as PriceData
        }));
      }

      await addInvestment(auth.currentUser.uid, investment);
      console.log('Investment added successfully');
      await fetchInvestments();
      setEditingInvestment(undefined);
      setTabValue(1);
      handleCloseForm();
    } catch (error) {
      console.error('Error adding investment:', error);
    }
  };

  const handleUpdateInvestment = async (id: string, investment: Partial<Investment>) => {
    try {
      await updateInvestment(id, investment, user.uid);
      await fetchInvestments(); // Update the list of investments
      setEditingInvestment(undefined);
      setIsEditFormOpen(false);
    } catch (error) {
      console.error('Error updating investment:', error);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const formatPrice = (token: string, priceData: PriceData | undefined) => {
    if (!priceData || priceData.price === undefined) return 'N/A';
    if (!token) return 'N/A';
    if (token.toUpperCase() === 'ADA') {
      return `€ ${priceData.price.toFixed(4)}`;
    }
    if (token.toUpperCase() === 'NIGHT') {
      return `€ ${priceData.price.toFixed(3)}`;
    }
    return `€ ${priceData.price.toFixed(2)}`;
  };

  const renderPriceChange = (priceData: PriceData | undefined) => {
    if (!priceData || priceData.price_change_percentage_24h === undefined) return null;
    const priceChange = priceData.price_change_percentage_24h;
    const color = priceChange >= 0 ? 'success.main' : 'error.main';
    const Icon = priceChange >= 0 ? ArrowDropUp : ArrowDropDown;
    return (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', ml: 1, color }}>
        <Icon />
        {Math.abs(priceChange).toFixed(2)}%
      </Box>
    );
  };

  const portfolioSummary = useMemo(() => {
    let totalInvestment = 0;
    let currentValue = 0;

    investments.forEach(investment => {
      if (!investment.token) return;
      const priceData = currentPrices[investment.token.toUpperCase()];

      totalInvestment += parseFloat(investment.investment);
      if (priceData) {
        currentValue += priceData.price * Number(investment.quantity);
      }
    });

    const profitLoss = currentValue - totalInvestment;
    const profitLossPercentage = (profitLoss / totalInvestment) * 100;

    return {
      totalInvestment,
      currentValue,
      profitLoss,
      profitLossPercentage
    };
  }, [investments, currentPrices]);

  const formatPurchasePrice = (token: string, price: number) => {
    if (price === undefined || price === null || isNaN(price)) return '0.00';
    if (!token) return Number(price).toFixed(2);
    if (token.toUpperCase() === 'ADA') return Number(price).toFixed(4);
    if (token.toUpperCase() === 'NIGHT') return Number(price).toFixed(3);
    return Number(price).toFixed(2);
  };

  const renderTokenWithLogo = (token: string) => {
    if (!token) return <Typography variant="body2">{token}</Typography>;
    const details = tokenDetails[token.toUpperCase()];
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {details && details.image ? (
          <Avatar src={details.image} alt={token} sx={{ width: 24, height: 24, mr: 1 }} />
        ) : (
          <Avatar sx={{ width: 24, height: 24, mr: 1 }}>{token.charAt(0)}</Avatar>
        )}
        <Typography variant="body2">{token}</Typography>
      </Box>
    );
  };

  const renderCard = (title: string, content: React.ReactNode, icon: React.ReactNode, infoTooltip: string) => (
    <Card
      sx={{
        mb: 3,
        borderRadius: designTokens.borderRadius.lg,
        background: designTokens.colors.background.paper,
        border: designTokens.glassmorphism.border,
        boxShadow: designTokens.shadows.md,
        overflow: 'hidden'
      }}
    >
      <Box sx={{
        background: designTokens.colors.primary.gradient,
        color: '#fff',
        p: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: designTokens.shadows.sm
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {React.cloneElement(icon as React.ReactElement, { sx: { color: '#fff' } })}
          <Typography variant="subtitle1" sx={{ fontWeight: designTokens.typography.fontWeight.bold }}>
            {title}
          </Typography>
        </Box>
        <Tooltip title={infoTooltip}>
          <IconButton size="small" sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: '#fff' } }}>
            <InfoIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <CardContent sx={{ p: 3 }}>
        {content}
      </CardContent>
    </Card>
  );

  const renderMobileCard = (title: string, content: React.ReactNode, icon: React.ReactNode, infoTooltip: string) => (
    <Card
      sx={{
        mb: 2,
        borderRadius: designTokens.borderRadius.lg,
        background: designTokens.colors.background.paper,
        border: designTokens.glassmorphism.border,
        boxShadow: designTokens.shadows.sm,
        overflow: 'hidden',
        width: '100%'
      }}
    >
      <Box sx={{
        background: designTokens.colors.primary.gradient,
        color: '#fff',
        p: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {React.cloneElement(icon as React.ReactElement, { sx: { color: '#fff', fontSize: '1.2rem' } })}
          <Typography variant="subtitle1" sx={{ fontWeight: designTokens.typography.fontWeight.bold, fontSize: '0.95rem' }}>
            {title}
          </Typography>
        </Box>
        <Tooltip title={infoTooltip}>
          <IconButton size="small" sx={{ color: 'rgba(255,255,255,0.8)' }}>
            <InfoIcon sx={{ fontSize: '1.1rem' }} />
          </IconButton>
        </Tooltip>
      </Box>
      <CardContent sx={{ p: 2, maxHeight: 'calc(100vh - 250px)', overflow: 'auto' }}>
        {content}
      </CardContent>
    </Card>
  );

  const renderMobileOverview = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 2 }}>
      {renderMobilePortfolioSummary()}
      {renderMobileCurrentPrices()}
      {renderMobileTotalsByToken()}
      {renderMobileForecastSimulation()}
      {renderMobileLongTermHoldings()}
    </Box>
  );

  const renderMobileLongTermHoldings = () => {
    const longTermHoldings = calculateLongTermHoldings();
    if (Object.keys(longTermHoldings).length === 0) return null;

    const totalValue = Object.values(longTermHoldings).reduce((acc, curr) => acc + curr.currentValue, 0);

    return (
      <Card sx={{ mb: 2, boxShadow: 3, borderRadius: 2, overflow: 'hidden', width: '100%', border: '1px solid', borderColor: 'success.main' }}>
        <Box sx={{
          background: 'linear-gradient(45deg, #2e7d32 30%, #4caf50 90%)',
          color: 'white',
          p: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Star fontSize="small" sx={{ color: 'gold' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', ml: 1 }}>
              <FormattedMessage id="taxExemptTokens" />
            </Typography>
          </Box>
          <Tooltip title={intl.formatMessage({ id: "taxExemptTokensInfo" })}>
            <InfoIcon fontSize="small" />
          </Tooltip>
        </Box>
        <CardContent sx={{ p: 1.5, maxHeight: 300, overflow: 'auto' }}>
          <TableContainer component={Paper} elevation={0}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell><FormattedMessage id="token" /></TableCell>
                  <TableCell><FormattedMessage id="quantity" /></TableCell>
                  <TableCell><FormattedMessage id="currentValue" /></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(longTermHoldings).map(([token, data]) => (
                  <TableRow key={token}>
                    <TableCell>{renderTokenWithLogo(token)}</TableCell>
                    <TableCell>{formatQuantity(token, data.quantity)}</TableCell>
                    <TableCell>{Math.floor(data.currentValue)} €</TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ backgroundColor: 'action.hover' }}>
                  <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}><FormattedMessage id="total" /></TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{Math.floor(totalValue)} €</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    );
  };

  const renderMobilePortfolioSummary = () => renderMobileCard(
    intl.formatMessage({ id: "portfolioSummary" }),
    <Grid container spacing={1} justifyContent="center">
      {[
        { icon: <AccountBalance fontSize="small" />, label: "totalInvestment", value: `€${Math.floor(portfolioSummary.totalInvestment).toLocaleString()}` },
        { icon: <MonetizationOn fontSize="small" />, label: "currentValue", value: `€${Math.floor(portfolioSummary.currentValue).toLocaleString()}` },
        { icon: <TrendingUp fontSize="small" />, label: "profitLoss", value: `€${portfolioSummary.profitLoss.toFixed(2).toLocaleString()} (${portfolioSummary.profitLossPercentage.toFixed(2)}%)` },
      ].map((item, index) => (
        <Grid item xs={4} key={index}>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            p: 1,
            borderRadius: 1,
            backgroundColor: 'background.paper',
            boxShadow: 1
          }}>
            {item.icon}
            <Typography variant="caption" color="text.secondary">
              <FormattedMessage id={item.label} />
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: item.label === "profitLoss" ? (portfolioSummary.profitLoss >= 0 ? 'success.main' : 'error.main') : 'inherit' }}>
              {item.value}
            </Typography>
          </Box>
        </Grid>
      ))}
    </Grid>,
    <AccountBalance fontSize="small" />,
    intl.formatMessage({ id: "portfolioSummaryInfo" })
  );

  const renderMobileCurrentPrices = () => {
    const uniqueTokens = Array.from(new Set(investments.map(inv => inv.token)));
    return renderMobileCard(
      intl.formatMessage({ id: "currentPrices" }),
      <Grid container spacing={1}>
        {uniqueTokens.map((token) => {
          const priceData = token ? currentPrices[token.toUpperCase()] : undefined;
          return (
            <Grid item xs={6} key={token}>
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                p: 1,
                borderRadius: 1,
                backgroundColor: 'background.paper',
                boxShadow: 1
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  {renderTokenWithLogo(token)}
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  {formatPrice(token, priceData)}
                </Typography>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  color: priceData && priceData.price_change_percentage_24h >= 0 ? 'success.main' : 'error.main'
                }}>
                  {renderPriceChange(priceData)}
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>,
      <ShowChart fontSize="small" />,
      intl.formatMessage({ id: "currentPricesInfo" })
    );
  };

  const renderMobileTotalsByToken = () => {
    const totals = calculateTotalsByToken();
    return renderMobileCard(
      intl.formatMessage({ id: "totalsByToken" }),
      <TableContainer component={Paper} sx={{ maxHeight: 300, overflow: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell><FormattedMessage id="token" /></TableCell>
              <TableCell><FormattedMessage id="totalQuantity" /></TableCell>
              <TableCell><FormattedMessage id="currentValue" /></TableCell>
              <TableCell><FormattedMessage id="profitLoss" /></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(totals).map(([token, data]) => {
              const currentPrice = token ? (currentPrices[token.toUpperCase()]?.price || 0) : 0;
              const currentValue = Math.floor(data.quantity * currentPrice);
              const profitLoss = Math.floor(currentValue - data.investment);
              const profitLossPercentage = (profitLoss / data.investment) * 100;
              return (
                <TableRow key={token}>
                  <TableCell>{renderTokenWithLogo(token)}</TableCell>
                  <TableCell>{formatQuantity(token, data.quantity)}</TableCell>
                  <TableCell>{currentValue} €</TableCell>
                  <TableCell>
                    <Box sx={{
                      color: profitLoss >= 0 ? 'success.main' : 'error.main',
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                      {profitLoss >= 0 ? <ArrowDropUp fontSize="small" /> : <ArrowDropDown fontSize="small" />}
                      <Typography variant="caption">
                        {profitLoss} € ({profitLossPercentage.toFixed(2)}%)
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>,
      <List fontSize="small" />,
      intl.formatMessage({ id: "portfolioInfo" })
    );
  };

  const renderMobileForecastSimulation = () => renderMobileCard(
    intl.formatMessage({ id: "forecastSimulation" }),
    <TableContainer component={Paper} sx={{ maxHeight: 300, overflow: 'auto' }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell><FormattedMessage id="token" /></TableCell>
            <TableCell><FormattedMessage id="forecastPrice" /></TableCell>
            <TableCell><FormattedMessage id="forecastValue" /></TableCell>
            <TableCell><FormattedMessage id="potentialProfit" /></TableCell>
            <TableCell><FormattedMessage id="growthVsCurrent" /></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.entries(calculateTotalsByToken()).map(([token, data]) => {
            const forecastPrice = parseFloat(forecastPrices[token] || '0');
            const currentPrice = currentPrices[token.toUpperCase()]?.price || 0;
            const forecastValue = Math.floor(data.quantity * forecastPrice);
            const potentialProfit = Math.floor(forecastValue - data.investment);
            const potentialProfitPercentage = (potentialProfit / data.investment) * 100;
            const growthVsCurrent = currentPrice > 0 ? ((forecastPrice - currentPrice) / currentPrice) * 100 : 0;
            return (
              <TableRow key={token}>
                <TableCell>{renderTokenWithLogo(token)}</TableCell>
                <TableCell>
                  <StyledTextField
                    size="small"
                    type="number"
                    inputMode="decimal"
                    value={forecastPrices[token] || ''}
                    onChange={(e) => handleForecastPriceChange(token, e.target.value)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">€</InputAdornment>,
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 4 } }}
                  />
                </TableCell>
                <TableCell>{forecastValue} €</TableCell>
                <TableCell>
                  <Box sx={{
                    color: potentialProfit >= 0 ? 'success.main' : 'error.main',
                    display: 'flex',
                    alignItems: 'center',
                  }}>
                    {potentialProfit >= 0 ? <ArrowDropUp fontSize="small" /> : <ArrowDropDown fontSize="small" />}
                    <Typography variant="caption">
                      {potentialProfit} € ({potentialProfitPercentage.toFixed(2)}%)
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{
                    color: growthVsCurrent >= 0 ? 'success.main' : 'error.main',
                    display: 'flex',
                    alignItems: 'center',
                  }}>
                    {growthVsCurrent >= 0 ? <ArrowDropUp fontSize="small" /> : <ArrowDropDown fontSize="small" />}
                    <Typography variant="caption">
                      {growthVsCurrent.toFixed(2)}%
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
          {Object.keys(calculateTotalsByToken()).length > 0 && (() => {
            const totalsByToken = calculateTotalsByToken();
            const totals = Object.entries(totalsByToken).reduce((acc, [token, data]) => {
              const forecastPrice = parseFloat(forecastPrices[token] || '0');
              const currentPrice = currentPrices[token.toUpperCase()]?.price || 0;
              const forecastValue = Math.floor(data.quantity * forecastPrice);
              const potentialProfit = Math.floor(forecastValue - data.investment);
              return {
                forecastValue: acc.forecastValue + forecastValue,
                potentialProfit: acc.potentialProfit + potentialProfit,
                totalInvestment: acc.totalInvestment + data.investment,
                currentValue: acc.currentValue + (currentPrice * data.quantity)
              };
            }, { forecastValue: 0, potentialProfit: 0, totalInvestment: 0, currentValue: 0 });
            const totalPotentialProfitPercentage = totals.totalInvestment > 0 ? (totals.potentialProfit / totals.totalInvestment) * 100 : 0;
            const totalGrowthVsCurrent = totals.currentValue > 0 ? ((totals.forecastValue - totals.currentValue) / totals.currentValue) * 100 : 0;

            return (
              <TableRow sx={{ backgroundColor: 'action.hover' }}>
                <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>
                  <FormattedMessage id="total" />
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  {totals.forecastValue} €
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <Box sx={{
                    color: totals.potentialProfit >= 0 ? 'success.main' : 'error.main',
                    display: 'flex',
                    alignItems: 'center',
                  }}>
                    {totals.potentialProfit >= 0 ? <ArrowDropUp fontSize="small" /> : <ArrowDropDown fontSize="small" />}
                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                      {totals.potentialProfit} € ({totalPotentialProfitPercentage.toFixed(2)}%)
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <Box sx={{
                    color: totalGrowthVsCurrent >= 0 ? 'success.main' : 'error.main',
                    display: 'flex',
                    alignItems: 'center',
                  }}>
                    {totalGrowthVsCurrent >= 0 ? <ArrowDropUp fontSize="small" /> : <ArrowDropDown fontSize="small" />}
                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                      {totalGrowthVsCurrent.toFixed(2)}%
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            );
          })()}
        </TableBody>
      </Table>
    </TableContainer>,
    <TrendingUp fontSize="small" />,
    intl.formatMessage({ id: "forecastSimulationInfo" })
  );

  const renderMobileInvestmentSimulation = () => renderMobileCard(
    intl.formatMessage({ id: "investmentSimulation" }),
    <TableContainer component={Paper} sx={{ maxHeight: 300, overflow: 'auto' }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell><FormattedMessage id="token" /></TableCell>
            <TableCell><FormattedMessage id="amount" /></TableCell>
            <TableCell><FormattedMessage id="received" /></TableCell>
            <TableCell><FormattedMessage id="totalTokens" /></TableCell>
            <TableCell><FormattedMessage id="newAvg" /></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.entries(calculateTotalsByToken()).map(([token, data]) => {

            const amount = parseFloat(simulationAmounts[token] || '0');
            const currentPrice = token ? (currentPrices[token.toUpperCase()]?.price || 0) : 0;

            let tokensReceived = 0;
            let newAvgPrice = 0;
            let oldAvgPrice = data.investment / data.quantity;
            let percentageChange = 0;

            if (amount > 0 && currentPrice > 0) {
              tokensReceived = amount / currentPrice;
              const newTotalQuantity = data.quantity + tokensReceived;
              const newTotalInvestment = data.investment + amount;
              newAvgPrice = newTotalInvestment / newTotalQuantity;
              percentageChange = ((newAvgPrice - oldAvgPrice) / oldAvgPrice) * 100;
            }

            return (
              <TableRow key={token}>
                <TableCell>{renderTokenWithLogo(token)}</TableCell>
                <TableCell>
                  <StyledTextField
                    size="small"
                    type="number"
                    inputMode="decimal"
                    value={simulationAmounts[token] || ''}
                    onChange={(e) => handleSimulationAmountChange(token, e.target.value)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">€</InputAdornment>,
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 4 } }}
                  />
                </TableCell>
                <TableCell>{tokensReceived > 0 ? formatQuantity(token, tokensReceived) : '-'}</TableCell>
                <TableCell>
                  {tokensReceived > 0 ? (
                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      {formatQuantity(token, data.quantity + tokensReceived)}
                    </Typography>
                  ) : '-'}
                </TableCell>
                <TableCell>
                  {newAvgPrice > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                        {token.toUpperCase() === 'ADA' ? newAvgPrice.toFixed(4) : newAvgPrice.toFixed(2)} €
                      </Typography>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        color: percentageChange < 0 ? 'success.main' : 'error.main'
                      }}>
                        {percentageChange < 0 ? <ArrowDropDown fontSize="small" /> : <ArrowDropUp fontSize="small" />}
                        <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                          {Math.abs(percentageChange).toFixed(2)}%
                        </Typography>
                      </Box>
                    </Box>
                  ) : '-'}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>,
    <Calculate fontSize="small" />,
    intl.formatMessage({ id: "investmentSimulationInfo" })
  );

  const renderMobileInvestmentList = () => renderMobileCard(
    intl.formatMessage({ id: "investmentList" }),
    <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 200px)', overflow: 'auto' }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell><FormattedMessage id="token" /></TableCell>
            <TableCell><FormattedMessage id="investment" /></TableCell>
            <TableCell><FormattedMessage id="quantity" /></TableCell>
            <TableCell><FormattedMessage id="purchasePrice" /></TableCell>
            <TableCell><FormattedMessage id="currentValue" /></TableCell>
            <TableCell><FormattedMessage id="actions" /></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {investments.map((investment) => {
            const priceData = investment.token ? currentPrices[investment.token.toUpperCase()] : undefined;
            const currentPrice = priceData ? priceData.price : 0;
            const currentValue = Math.floor(currentPrice * Number(investment.quantity));
            return (
              <TableRow key={investment.id}>
                <TableCell>{renderTokenWithLogo(investment.token)}</TableCell>
                <TableCell>{Math.floor(parseFloat(investment.investment))} €</TableCell>
                <TableCell>{formatQuantity(investment.token, Number(investment.quantity))}</TableCell>
                <TableCell>{formatPurchasePrice(investment.token, investment.purchasePrice)} €</TableCell>
                <TableCell>{currentValue} €</TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleEdit(investment)}>
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(investment.id)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>,
    <List fontSize="small" />,
    intl.formatMessage({ id: "investmentListInfo" })
  );

  const renderCurrentPrices = () => {
    const uniqueTokens = Array.from(new Set(investments.map(inv => inv.token)));
    return renderCard(
      intl.formatMessage({ id: "currentPrices" }),
      <Grid container spacing={2}>
        {uniqueTokens.map((token) => {
          const priceData = token ? currentPrices[token.toUpperCase()] : undefined;
          return (
            <Grid item xs={12} sm={6} md={4} key={token}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                p: 2,
                borderRadius: 2,
                backgroundColor: 'background.paper',
                boxShadow: 1
              }}>
                <Box sx={{ mr: 2, minWidth: 40 }}>
                  {renderTokenWithLogo(token)}
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {formatPrice(token, priceData)}
                  </Typography>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    color: priceData && priceData.price_change_percentage_24h >= 0 ? 'success.main' : 'error.main'
                  }}>
                    {priceData && priceData.price_change_percentage_24h !== undefined && (
                      <>
                        {priceData.price_change_percentage_24h >= 0 ? <ArrowDropUp fontSize="small" /> : <ArrowDropDown fontSize="small" />}
                        <Typography variant="body2">
                          {Math.abs(priceData.price_change_percentage_24h).toFixed(2)}%
                        </Typography>
                      </>
                    )}
                  </Box>
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>,
      <ShowChart fontSize="small" />,
      intl.formatMessage({ id: "currentPricesInfo" })
    );
  };

  const formatQuantity = (token: string, quantity: number) => {
    if (quantity === undefined || quantity === null || isNaN(quantity)) return '0.00';
    if (!token) return Number(quantity).toFixed(2);
    if (token.toUpperCase() === 'BTC') {
      return Number(quantity).toFixed(4);
    }
    return Number(quantity).toFixed(2);
  };

  const calculateTotalsByToken = () => {
    const totals: { [key: string]: { quantity: number, investment: number } } = {};
    investments.forEach((investment) => {
      if (!totals[investment.token]) {
        totals[investment.token] = { quantity: 0, investment: 0 };
      }
      totals[investment.token].quantity += Number(investment.quantity);
      totals[investment.token].investment += parseFloat(investment.investment);
    });
    return totals;
  };

  const calculateLongTermHoldings = () => {
    const longTermHoldings: { [key: string]: { quantity: number, totalInvestment: number, currentValue: number } } = {};
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

    investments.forEach((investment) => {
      if (investment.purchaseDate && new Date(investment.purchaseDate) < oneYearAgo) {
        if (!longTermHoldings[investment.token]) {
          longTermHoldings[investment.token] = { quantity: 0, totalInvestment: 0, currentValue: 0 };
        }
        longTermHoldings[investment.token].quantity += Number(investment.quantity);
        longTermHoldings[investment.token].totalInvestment += parseFloat(investment.investment);

        const priceData = currentPrices[investment.token.toUpperCase()];
        const currentPrice = priceData ? priceData.price : 0;
        longTermHoldings[investment.token].currentValue += currentPrice * Number(investment.quantity);
      }
    });
    return longTermHoldings;
  };

  const renderTotalsByToken = () => {
    const totals = calculateTotalsByToken();
    return renderCard(
      intl.formatMessage({ id: "totalsByToken" }),
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'background.default' }}>
              <TableCell sx={{ fontWeight: 'bold' }}><FormattedMessage id="token" /></TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}><FormattedMessage id="totalQuantity" /></TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}><FormattedMessage id="totalInvestment" /></TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}><FormattedMessage id="averagePurchasePrice" /></TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}><FormattedMessage id="currentPrice" /></TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}><FormattedMessage id="currentValue" /></TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}><FormattedMessage id="profitLoss" /></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(totals).map(([token, data]) => {
              const currentPrice = currentPrices[token.toUpperCase()]?.price || 0;
              const currentValue = Math.floor(data.quantity * currentPrice);
              const profitLoss = Math.floor(currentValue - data.investment);
              const profitLossPercentage = (profitLoss / data.investment) * 100;
              const averagePurchasePrice = data.investment / data.quantity;
              return (
                <TableRow key={token} sx={{ '&:nth-of-type(odd)': { backgroundColor: 'action.hover' } }}>
                  <TableCell>{renderTokenWithLogo(token)}</TableCell>
                  <TableCell>{formatQuantity(token, data.quantity)}</TableCell>
                  <TableCell>{Math.floor(data.investment)} €</TableCell>
                  <TableCell>
                    {token.toUpperCase() === 'ADA'
                      ? averagePurchasePrice.toFixed(4)
                      : token.toUpperCase() === 'NIGHT'
                        ? averagePurchasePrice.toFixed(3)
                        : averagePurchasePrice.toFixed(2)} €
                  </TableCell>
                  <TableCell>{formatPrice(token, currentPrices[token.toUpperCase()])}</TableCell>
                  <TableCell>{currentValue} €</TableCell>
                  <TableCell>
                    <Box sx={{
                      color: profitLoss >= 0 ? 'success.main' : 'error.main',
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                      {profitLoss >= 0 ? <ArrowDropUp fontSize="small" /> : <ArrowDropDown fontSize="small" />}
                      <Typography variant="body2">
                        {profitLoss} € ({profitLossPercentage.toFixed(2)}%)
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>,
      <List fontSize="small" />,
      intl.formatMessage({ id: "portfolioInfo" })
    );
  };

  const renderLongTermHoldings = () => {
    const longTermHoldings = calculateLongTermHoldings();
    if (Object.keys(longTermHoldings).length === 0) return null;

    const summary = Object.values(longTermHoldings).reduce((acc, curr) => ({
      currentValue: acc.currentValue + curr.currentValue,
      totalInvestment: acc.totalInvestment + curr.totalInvestment
    }), { currentValue: 0, totalInvestment: 0 });

    const totalProfitLoss = summary.currentValue - summary.totalInvestment;
    const totalProfitLossPercentage = summary.totalInvestment > 0 ? (totalProfitLoss / summary.totalInvestment) * 100 : 0;

    return (
      <Card sx={{ mb: 2, boxShadow: 3, borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'success.main' }}>
        <Box sx={{
          background: 'linear-gradient(45deg, #2e7d32 30%, #4caf50 90%)',
          color: 'white',
          p: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Star fontSize="small" sx={{ color: 'gold' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', ml: 1 }}>
              <FormattedMessage id="taxExemptTokens" />
            </Typography>
          </Box>
          <Tooltip title={intl.formatMessage({ id: "taxExemptTokensInfo" })}>
            <InfoIcon fontSize="small" />
          </Tooltip>
        </Box>
        <CardContent sx={{ p: 1.5 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'background.default' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}><FormattedMessage id="token" /></TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}><FormattedMessage id="quantity" /></TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}><FormattedMessage id="currentValue" /></TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}><FormattedMessage id="profitLoss" /></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(longTermHoldings).map(([token, data]) => {
                  const profitLoss = data.currentValue - data.totalInvestment;
                  const profitLossPercentage = data.totalInvestment > 0 ? (profitLoss / data.totalInvestment) * 100 : 0;

                  return (
                    <TableRow key={token} sx={{ '&:nth-of-type(odd)': { backgroundColor: 'action.hover' } }}>
                      <TableCell>{renderTokenWithLogo(token)}</TableCell>
                      <TableCell>{formatQuantity(token, data.quantity)}</TableCell>
                      <TableCell>{Math.floor(data.currentValue)} €</TableCell>
                      <TableCell>
                        <Box sx={{
                          color: profitLoss >= 0 ? 'success.main' : 'error.main',
                          display: 'flex',
                          alignItems: 'center',
                        }}>
                          {profitLoss >= 0 ? <ArrowDropUp fontSize="small" /> : <ArrowDropDown fontSize="small" />}
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {Math.floor(profitLoss)} € ({profitLossPercentage.toFixed(2)}%)
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
                <TableRow sx={{ backgroundColor: 'action.selected' }}>
                  <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}><FormattedMessage id="total" /></TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{Math.floor(summary.currentValue)} €</TableCell>
                  <TableCell>
                    <Box sx={{
                      color: totalProfitLoss >= 0 ? 'success.main' : 'error.main',
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                      {totalProfitLoss >= 0 ? <ArrowDropUp fontSize="small" /> : <ArrowDropDown fontSize="small" />}
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {Math.floor(totalProfitLoss)} € ({totalProfitLossPercentage.toFixed(2)}%)
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    );
  };

  const renderPortfolioSummary = () => renderCard(
    intl.formatMessage({ id: "portfolioSummary" }),
    <Grid container spacing={3}>
      {[
        { icon: <AccountBalance sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />, label: "totalInvestment", value: `€${Math.floor(portfolioSummary.totalInvestment).toLocaleString()}` },
        { icon: <MonetizationOn sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />, label: "currentValue", value: `€${Math.floor(portfolioSummary.currentValue).toLocaleString()}` },
        { icon: <TrendingUp sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />, label: "profitLoss", value: `€${portfolioSummary.profitLoss.toFixed(2).toLocaleString()} (${portfolioSummary.profitLossPercentage.toFixed(2)}%)` },
      ].map((item, index) => (
        <Grid item xs={12} sm={4} key={index}>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            p: 2,
            borderRadius: 2,
            backgroundColor: 'background.paper',
            boxShadow: 1
          }}>
            {item.icon}
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <FormattedMessage id={item.label} />
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: item.label === "profitLoss" ? (portfolioSummary.profitLoss >= 0 ? 'success.main' : 'error.main') : 'inherit' }}>
              {item.value}
            </Typography>
          </Box>
        </Grid>
      ))}
    </Grid>,
    <AccountBalance fontSize="small" />,
    intl.formatMessage({ id: "portfolioSummaryInfo" })
  );

  const handleForecastPriceChange = (token: string, value: string) => {
    let price = value.replace(',', '.');
    if (token.toUpperCase() === 'ADA') {
      // Limit to 4 decimal places for ADA
      price = price.match(/^\d*\.?\d{0,4}$/)?.[0] || '';
    } else if (token.toUpperCase() === 'NIGHT') {
      // Limit to 3 decimal places for NIGHT
      price = price.match(/^\d*\.?\d{0,3}$/)?.[0] || '';
    } else {
      // Limit to 2 decimal places for other tokens
      price = price.match(/^\d*\.?\d{0,2}$/)?.[0] || '';
    }
    setForecastPrices(prev => ({ ...prev, [token]: price }));
  };

  const handleSimulationAmountChange = (token: string, value: string) => {
    let amount = value.replace(',', '.');
    // Limit to 2 decimal places for investment amount
    amount = amount.match(/^\d*\.?\d{0,2}$/)?.[0] || '';
    setSimulationAmounts(prev => ({ ...prev, [token]: amount }));
  };

  const renderForecastSimulation = () => {
    const totals = calculateTotalsByToken();
    return renderCard(
      intl.formatMessage({ id: "forecastSimulation" }),
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'background.default' }}>
              <TableCell sx={{ fontWeight: 'bold' }}><FormattedMessage id="token" /></TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}><FormattedMessage id="totalQuantity" /></TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}><FormattedMessage id="forecastPrice" /></TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}><FormattedMessage id="forecastValue" /></TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}><FormattedMessage id="potentialProfit" /></TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}><FormattedMessage id="growthVsCurrent" /></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(totals).map(([token, data]) => {
              const forecastPrice = parseFloat(forecastPrices[token] || '0');
              const currentPrice = currentPrices[token.toUpperCase()]?.price || 0;
              const forecastValue = Math.floor(data.quantity * forecastPrice);
              const potentialProfit = Math.floor(forecastValue - data.investment);
              const potentialProfitPercentage = (potentialProfit / data.investment) * 100;
              const growthVsCurrent = currentPrice > 0 ? ((forecastPrice - currentPrice) / currentPrice) * 100 : 0;
              return (
                <TableRow key={token} sx={{ '&:nth-of-type(odd)': { backgroundColor: 'action.hover' } }}>
                  <TableCell>{renderTokenWithLogo(token)}</TableCell>
                  <TableCell>{formatQuantity(token, data.quantity)}</TableCell>
                  <TableCell>
                    <StyledTextField
                      size="small"
                      type="number"
                      inputMode="decimal"
                      value={forecastPrices[token] || ''}
                      onChange={(e) => handleForecastPriceChange(token, e.target.value)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">€</InputAdornment>,
                      }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 4 } }}
                    />
                  </TableCell>
                  <TableCell>{forecastValue} €</TableCell>
                  <TableCell>
                    <Box sx={{
                      color: potentialProfit >= 0 ? 'success.main' : 'error.main',
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                      {potentialProfit >= 0 ? <ArrowDropUp fontSize="small" /> : <ArrowDropDown fontSize="small" />}
                      <Typography variant="body2">
                        {potentialProfit} € ({potentialProfitPercentage.toFixed(2)}%)
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{
                      color: growthVsCurrent >= 0 ? 'success.main' : 'error.main',
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                      {growthVsCurrent >= 0 ? <ArrowDropUp fontSize="small" /> : <ArrowDropDown fontSize="small" />}
                      <Typography variant="body2">
                        {growthVsCurrent.toFixed(2)}%
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
            {Object.keys(totals).length > 0 && (() => {
              const summary = Object.entries(totals).reduce((acc, [token, data]) => {
                const forecastPrice = parseFloat(forecastPrices[token] || '0');
                const currentPrice = currentPrices[token.toUpperCase()]?.price || 0;
                const forecastValue = Math.floor(data.quantity * forecastPrice);
                const potentialProfit = Math.floor(forecastValue - data.investment);
                return {
                  forecastValue: acc.forecastValue + forecastValue,
                  potentialProfit: acc.potentialProfit + potentialProfit,
                  totalInvestment: acc.totalInvestment + data.investment,
                  currentValue: acc.currentValue + (currentPrice * data.quantity)
                };
              }, { forecastValue: 0, potentialProfit: 0, totalInvestment: 0, currentValue: 0 });
              const totalPotentialProfitPercentage = summary.totalInvestment > 0 ? (summary.potentialProfit / summary.totalInvestment) * 100 : 0;
              const totalGrowthVsCurrent = summary.currentValue > 0 ? ((summary.forecastValue - summary.currentValue) / summary.currentValue) * 100 : 0;

              return (
                <TableRow sx={{ backgroundColor: 'action.selected' }}>
                  <TableCell colSpan={3} sx={{ fontWeight: 'bold' }}>
                    <FormattedMessage id="total" />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    {summary.forecastValue} €
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    <Box sx={{
                      color: summary.potentialProfit >= 0 ? 'success.main' : 'error.main',
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                      {summary.potentialProfit >= 0 ? <ArrowDropUp fontSize="small" /> : <ArrowDropDown fontSize="small" />}
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {summary.potentialProfit} € ({totalPotentialProfitPercentage.toFixed(2)}%)
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    <Box sx={{
                      color: totalGrowthVsCurrent >= 0 ? 'success.main' : 'error.main',
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                      {totalGrowthVsCurrent >= 0 ? <ArrowDropUp fontSize="small" /> : <ArrowDropDown fontSize="small" />}
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {totalGrowthVsCurrent.toFixed(2)}%
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })()}
          </TableBody>
        </Table>
      </TableContainer>,
      <TrendingUp fontSize="small" />,
      intl.formatMessage({ id: "forecastSimulationInfo" })
    );
  };

  const renderInvestmentSimulation = () => {
    const totals = calculateTotalsByToken();
    return renderCard(
      intl.formatMessage({ id: "investmentSimulation" }),
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'background.default' }}>
              <TableCell sx={{ fontWeight: 'bold' }}><FormattedMessage id="token" /></TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}><FormattedMessage id="investmentAmount" /></TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}><FormattedMessage id="tokensReceived" /></TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}><FormattedMessage id="totalTokens" /></TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}><FormattedMessage id="newAveragePrice" /></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(totals).map(([token, data]) => {
              const amount = parseFloat(simulationAmounts[token] || '0');
              const currentPrice = token ? (currentPrices[token.toUpperCase()]?.price || 0) : 0;

              let tokensReceived = 0;
              let newAvgPrice = 0;
              let oldAvgPrice = data.investment / data.quantity;
              let percentageChange = 0;

              if (amount > 0 && currentPrice > 0) {
                tokensReceived = amount / currentPrice;
                const newTotalQuantity = data.quantity + tokensReceived;
                const newTotalInvestment = data.investment + amount;
                newAvgPrice = newTotalInvestment / newTotalQuantity;
                percentageChange = ((newAvgPrice - oldAvgPrice) / oldAvgPrice) * 100;
              }

              return (
                <TableRow key={token} sx={{ '&:nth-of-type(odd)': { backgroundColor: 'action.hover' } }}>
                  <TableCell>{renderTokenWithLogo(token)}</TableCell>
                  <TableCell>
                    <StyledTextField
                      size="small"
                      type="number"
                      inputMode="decimal"
                      value={simulationAmounts[token] || ''}
                      onChange={(e) => handleSimulationAmountChange(token, e.target.value)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">€</InputAdornment>,
                      }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 4 } }}
                    />
                  </TableCell>
                  <TableCell>{tokensReceived > 0 ? formatQuantity(token, tokensReceived) : '-'}</TableCell>
                  <TableCell>
                    {tokensReceived > 0 ? (
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        {formatQuantity(token, data.quantity + tokensReceived)}
                      </Typography>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    {newAvgPrice > 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {token.toUpperCase() === 'ADA'
                            ? newAvgPrice.toFixed(4)
                            : token.toUpperCase() === 'NIGHT'
                              ? newAvgPrice.toFixed(3)
                              : newAvgPrice.toFixed(2)} €
                        </Typography>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          color: percentageChange < 0 ? 'success.main' : 'error.main'
                        }}>
                          {percentageChange < 0 ? <ArrowDropDown fontSize="small" /> : <ArrowDropUp fontSize="small" />}
                          <Typography variant="caption">
                            {Math.abs(percentageChange).toFixed(2)}%
                          </Typography>
                        </Box>
                      </Box>
                    ) : '-'}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>,
      <Calculate fontSize="small" />,
      intl.formatMessage({ id: "investmentSimulationInfo" })
    );
  };

  const handleSort = (key: 'investment' | 'purchasePrice' | 'currentValue' | 'profitLoss' | 'purchaseDate') => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedInvestments = useMemo(() => {
    let sortableInvestments = [...investments];
    if (sortConfig !== null) {
      sortableInvestments.sort((a, b) => {
        let aValue: number | string = 0;
        let bValue: number | string = 0;

        const getValues = (inv: Investment) => {
          const priceData = inv.token ? currentPrices[inv.token.toUpperCase()] : undefined;
          const currentPrice = priceData ? priceData.price : 0;
          const currentValue = currentPrice * Number(inv.quantity);
          const investmentAmount = parseFloat(inv.investment);

          switch (sortConfig.key) {
            case 'investment':
              return investmentAmount;
            case 'purchasePrice':
              return inv.purchasePrice;
            case 'currentValue':
              return currentValue;
            case 'profitLoss':
              return currentValue - investmentAmount;
            case 'purchaseDate':
              return inv.purchaseDate ? new Date(inv.purchaseDate).getTime() : 0;
            default:
              return 0;
          }
        };

        aValue = getValues(a);
        bValue = getValues(b);

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableInvestments;
  }, [investments, sortConfig, currentPrices]);

  const renderInvestmentList = () => renderCard(
    intl.formatMessage({ id: "investmentList" }),
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell><FormattedMessage id="token" /></TableCell>
            <TableCell>
              <TableSortLabel
                active={sortConfig?.key === 'purchaseDate'}
                direction={sortConfig?.key === 'purchaseDate' ? sortConfig.direction : 'asc'}
                onClick={() => handleSort('purchaseDate')}
              >
                <FormattedMessage id="purchaseDate" />
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortConfig?.key === 'investment'}
                direction={sortConfig?.key === 'investment' ? sortConfig.direction : 'asc'}
                onClick={() => handleSort('investment')}
              >
                <FormattedMessage id="investment" />
              </TableSortLabel>
            </TableCell>
            <TableCell><FormattedMessage id="quantity" /></TableCell>
            <TableCell>
              <TableSortLabel
                active={sortConfig?.key === 'purchasePrice'}
                direction={sortConfig?.key === 'purchasePrice' ? sortConfig.direction : 'asc'}
                onClick={() => handleSort('purchasePrice')}
              >
                <FormattedMessage id="purchasePrice" />
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortConfig?.key === 'currentValue'}
                direction={sortConfig?.key === 'currentValue' ? sortConfig.direction : 'asc'}
                onClick={() => handleSort('currentValue')}
              >
                <FormattedMessage id="currentValue" />
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortConfig?.key === 'profitLoss'}
                direction={sortConfig?.key === 'profitLoss' ? sortConfig.direction : 'asc'}
                onClick={() => handleSort('profitLoss')}
              >
                <FormattedMessage id="profitLoss" />
              </TableSortLabel>
            </TableCell>
            <TableCell><FormattedMessage id="actions" /></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedInvestments.map((investment) => {
            const priceData = investment.token ? currentPrices[investment.token.toUpperCase()] : undefined;
            const currentPrice = priceData ? priceData.price : 0;
            const currentValue = Math.floor(currentPrice * Number(investment.quantity));
            const profitLoss = Math.floor(currentValue - parseFloat(investment.investment));
            const profitLossPercentage = (profitLoss / parseFloat(investment.investment)) * 100;

            return (
              <TableRow key={investment.id}>
                <TableCell>{renderTokenWithLogo(investment.token)}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {investment.purchaseDate ? new Date(investment.purchaseDate).toLocaleDateString() : '-'}
                    {investment.purchaseDate && (new Date().getTime() - new Date(investment.purchaseDate).getTime()) / (1000 * 3600 * 24) > 365 && (
                      <Tooltip title={intl.formatMessage({ id: "heldOverYear" })}>
                        <Star fontSize="small" sx={{ color: 'warning.main', ml: 1 }} />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell>{Math.floor(parseFloat(investment.investment))} €</TableCell>
                <TableCell>{formatQuantity(investment.token, Number(investment.quantity))}</TableCell>
                <TableCell>{formatPurchasePrice(investment.token, investment.purchasePrice)} €</TableCell>
                <TableCell>{currentValue} €</TableCell>
                <TableCell>
                  <Box sx={{
                    color: profitLoss >= 0 ? 'success.main' : 'error.main',
                    display: 'flex',
                    alignItems: 'center',
                  }}>
                    {profitLoss >= 0 ? <ArrowDropUp fontSize="small" /> : <ArrowDropDown fontSize="small" />}
                    <Typography variant="body2">
                      {profitLoss} € ({profitLossPercentage.toFixed(2)}%)
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleEdit(investment)}>
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(investment.id)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>,
    <List fontSize="small" />,
    intl.formatMessage({ id: "investmentListInfo" })
  );


  if (isLoading) {
    return <InvestmentSkeleton />;
  }

  return (
    <Container maxWidth="lg" disableGutters={isMobile} sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {isMobile ? (
        <>
          <Box sx={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: 'background.paper' }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{
                mb: 2,
                borderBottom: 1,
                borderColor: 'divider',
                '& .MuiTab-root': { minHeight: '48px' }
              }}
            >
              <Tab icon={<ShowChart />} label={<FormattedMessage id="overview" />} />
              <Tab icon={<List />} label={<FormattedMessage id="details" />} />
            </Tabs>
          </Box>
          <Box sx={{ flexGrow: 1, overflow: 'auto', px: 1, pb: 2 }}>
            {tabValue === 0 && (
              <>
                {renderMobilePortfolioSummary()}
                {renderMobileCurrentPrices()}
                {renderMobileTotalsByToken()}
                {renderMobileInvestmentSimulation()}
                {renderMobileForecastSimulation()}
              </>
            )}
            {tabValue === 1 && (
              <>
                {renderMobileInvestmentList()}
              </>
            )}
          </Box>
        </>
      ) : (
        <Box sx={{ my: 4 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              {renderPortfolioSummary()}
              {renderCurrentPrices()}
              {renderTotalsByToken()}
              {renderInvestmentSimulation()}
              {renderForecastSimulation()}
              {renderInvestmentList()}
              {renderLongTermHoldings()}
            </Grid>
          </Grid>
        </Box>
      )}
      <Fab
        color="primary"
        aria-label="add"
        onClick={handleOpenForm}
        size="small"
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
        }}
      >
        <AddIcon fontSize="small" />
      </Fab>
      <Dialog open={isFormOpen} onClose={handleCloseForm} fullWidth maxWidth="sm">
        <DialogTitle>
          <FormattedMessage id="addInvestment" />
        </DialogTitle>
        <DialogContent>
          <InvestmentForm
            onAddInvestment={handleAddInvestment}
            onUpdateInvestment={handleUpdateInvestment}
            userId={user.uid}
            onCancel={handleCloseForm}
          />
        </DialogContent>
      </Dialog>
      <Dialog open={isEditFormOpen} onClose={handleCloseEditForm} fullWidth maxWidth="sm">
        <DialogTitle>
          <FormattedMessage id="editInvestment" />
        </DialogTitle>
        <DialogContent>
          <InvestmentForm
            onAddInvestment={handleAddInvestment}
            onUpdateInvestment={handleUpdateInvestment}
            investment={editingInvestment}
            userId={user.uid}
            onCancel={handleCloseEditForm}
          />
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default InvestmentList;